import { css } from 'astroturf';
import React from 'react';

import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import Link from '../link';

import { currentPolypaneChromeVersion } from '../../data/releaseData';

const styles = css`
  .faq {
    scroll-margin-top: 5rem;
    max-width: 800px;
    margin: 10rem auto 5rem;

    & h3 {
      text-align: center;
      font-weight: 900;
      font-size: clamp(18px, 16px + 4vw, 44px);
      line-height: 1.25;
      margin: auto;
      margin-bottom: 1.5rem;
      margin-top: 0;
      letter-spacing: -0.015em;
      max-width: 60rem;
      color: rgb(10, 21, 48);
    }
  }

  .item + .item {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }

  .header {
    padding: 1rem;
    cursor: pointer;
    color: rgb(10, 21, 48);
    font-size: 1.125rem;
    transition: 0.25s ease-in-out color;

    &:hocus {
      color: #000;
    }

    &::before {
      display: inline-block;
      content: '';
      height: 10px;
      width: 10px;
      margin-right: 12px;
      border-bottom: 2px solid currentColor;
      border-right: 2px solid currentColor;
      transform: rotate(-45deg);
      transition: 0.25s ease-in-out transform;
    }

    &[aria-expanded='true']::before,
    &[aria-selected='true']::before {
      transform: rotate(45deg) translateY(-0.25rem);
    }
  }

  .content {
    interpolate-size: allow-keywords;
    padding: 0rem 1rem 2rem 2.5rem;

    opacity: 1;
    height: auto;

    transition:
      content-visibility 0.25s allow-discrete,
      0.25s ease-in-out height,
      0.25s ease-in-out opacity,
      0.25s ease-in-out padding-bottom;

    &[hidden] {
      interpolate-size: allow-keywords;
      opacity: 0;
      height: 0;
      overflow: hidden;
      display: block;
      padding-bottom: 0;
    }

    & button {
      color: rgb(0, 121, 177);
      font-size: inherit;
      font-weight: inherit;
      border: 0;
      margin: 0;
      background: none;
    }
  }
`;

class FAQ extends React.Component {
  render() {
    return (
      <div className={styles.faq} id="faq">
        <h3>Frequently Asked Questions</h3>
        <Accordion allowMultipleExpanded allowZeroExpanded>
          {faqs.map((faq) => (
            <AccordionItem className={styles.item} key={faq.q}>
              <AccordionItemHeading>
                <AccordionItemButton className={styles.header}>{faq.q}</AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel className={styles.content}>
                <div className="childPadding">{faq.a}</div>
              </AccordionItemPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    );
  }
}

export default FAQ;

const faqs = [
  {
    q: 'What rendering engine does Polypane use?',
    a: (
      <>
        Polypane is based on Chromium version {currentPolypaneChromeVersion}, and is continuously updated as Chromium
        versions get released (We update with even-numbered Chromium versions).
      </>
    ),
  },
  {
    q: 'What operating systems does Polypane support?',
    a: (
      <>
        Polypane runs on Windows, Mac and Linux. We have universal builds to support Intel, Apple Silicon and ARM
        processors. For Linux we have both a .deb and .AppImage available.
        <br />
        <br />
        Our Windows builds are signed with an Extended Validation code signing certificate, and our Mac builds are
        signed and notarized by Apple.
      </>
    ),
  },
  {
    q: 'How does Polypane work?',
    a: (
      <>
        Polypane runs multiple Chromium instances of your site, each with its own emulation settings like dark mode,
        touch, slow network etc. It syncs all interactions like scrolling, form filling, hovers, and focus, so you only
        need to interact with one instances to see the effect in all.
        <br />
        <br />
        Edits in the inspector also apply to all instances at once, letting you see changes immediately. At the same
        time, Polypane analyzes your CSS, HTML, storage, metadata, and more to automatically lint your entire page and
        gives you feedback on how to improve it.
      </>
    ),
  },
  {
    q: 'When is Polypane a bad fit?',
    a: (
      <>
        Polypane is a browser built for developers, so it assumes your website is reasonably well built. It does not try
        to compensate for slow code (which becomes noticable when you run the same slow code simultaneously half a dozen
        times).
        <br />
        <br />
        Because Polypane is based on Chromium, it also will not help you with issues specific to Safari or Firefox.{' '}
        <strong>Even if you use a developer browser, you should still test in multiple rendering engines</strong>.
      </>
    ),
  },
  {
    q: 'Can I test all browsers in Polypane?',
    a: (
      <p>
        No, but also yes. It's not possible to show all rendering engines in a single, cross-platform browser. Under the
        hood Polypane uses the Chromium engine, the same as Chrome, Edge, Brave and many other browsers. It renders
        sites the same as the vast majority of browsers.
        <br />
        <br />
        Polypane can also <em>emulate</em> other browsers like Safari for iOS or Firefox. This means your site's JS will
        think it's running in them and you can use that to test specific fallback logic. The underlying rendering engine
        is still Chromium though, so a real Safari or Firefox <em>can</em> render things differently. <br />
        <br />
        <strong>Even if you use a developer browser, you should still test in multiple rendering engines</strong>.
        That's what <Link to="/portal/">Polypane Portal</Link> lets you do: open the Portal URL in any browser or device
        you have, and all of them are synced with Polypane.
      </p>
    ),
  },
  {
    q: 'What is your roadmap?',
    a: (
      <>
        We do not publish a public roadmap, but are very responsive to user requests and input. User requests are added
        as votes to our (extensive, 5+ year) internal roadmap and new features are planned according to popularity.
      </>
    ),
  },
  {
    q: 'I have more questions!',
    a: (
      <>
        I mean, that's more of a remark, but you can find much more about Polypane in our <Link to="/docs/">docs</Link>,
        or ask questions to us directly in our <i>non-AI, human-operated</i>{' '}
        <button
          className="link-like"
          type="button"
          onClick={() => {
            if (typeof window !== 'undefined' && !!window.$crisp) {
              window.$crisp.push(['do', 'chat:open']);
            } else {
              alert("It seems you're blocking Crisp, our chat provider. Please use one of the other options!");
            }
          }}
        >
          chat widget
        </button>
        , on <a href="https://bsky.app/profile/polypane.app">Bluesky</a>, in our <Link to="/support/">Slack</Link> or{' '}
        <Link to="/support/">e-mail us</Link>.
      </>
    ),
  },
];
